<script setup>
import { computed, onMounted, ref } from 'vue';
import PictureLazy from '@maison/components/common/PictureLazy.vue';
import Slider from '@maison/components/organisms/slider/Slider.vue';
import { PhxProductCard, PhxSpecialModelsModal, PhxTabPanel, PhxTextCard } from '@phx-styleguides/iwc';
import BackgroundVideo from '@maison/components/molecules/BackgroundVideo.vue';
import '@sg/src/View/Components/Atoms/GridLayout/grid-layout.css';
import '@sg-core/src/View/Components/Atoms/Grid/grid.css';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    default: null,
  },
  logo: {
    type: Object,
    default: null,
  },
  slides: {
    type: Array,
    required: true,
  },
  tabs: {
    type: Array,
    default: () => [],
  },
});

const modalRef = ref(null);

const getSlideComponent = (slide) => {
  switch (slide.template) {
    case 'product-card':
      return PhxProductCard;
    case 'picture':
      return PictureLazy;
    case 'background-video':
      return BackgroundVideo;
  }
};

const hasModals = computed(() => props.tabs.length > 0);

onMounted(() => {
  const triggers = document.getElementById(`${props.id}-slider`).querySelectorAll('[data-special-models-trigger]');

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const tabValue = trigger.dataset.specialModelsTrigger;
      modalRef.value.openModal(tabValue);
    });
  });
});
</script>

<template>
  <div class="text-card special-models__text-card">
    <div class="text-card__heading phxsg-fade-in">
      <h3 class="text-card__title heading-small">{{ title }}</h3>
    </div>

    <div class="text-card__content phxsg-fade-in">
      <div class="rich-text" v-html="text" />
    </div>
  </div>

  <div class="picture special-models__logo phxsg-fade-in">
    <picture-lazy v-if="logo" v-bind="logo" />
  </div>

  <div class="special-models__slider phxsg-fade-in-up">
    <slider :id="`${id}-slider`">
      <template #body>
        <div
          v-for="slide in slides"
          :key="slide.id"
          class="slider__item"
          role="tabpanel"
          aria-labelledby="pagination-1"
        >
          <component :is="getSlideComponent(slide)" v-bind="slide.data" />
        </div>
      </template>
    </slider>
  </div>

  <teleport to="#teleport-target-modals">
    <phx-special-models-modal v-if="hasModals" :id="`${id}-modal`" ref="modalRef" :tabs="tabs">
      <phx-tab-panel
        v-for="(tab, i) in tabs"
        :id="tab.id"
        :key="`${id}-tabs-${i}`"
        class="special-models-modal__tab-panel phxsg-grid__items phxsg-grid-layout phxsg-grid--spacing-3"
        :label="tab.name"
        :short-label="String(i + 1).padStart(2, '0')"
      >
        <div class="special-models-modal__background">
          <picture-lazy
            v-bind="tab.background.templateData"
            class="picture picture--cover phxsg-scale-down phxsg-is-revealed"
          />
        </div>

        <div class="special-models-modal__tab-panel-content">
          <div class="special-models-modal__watch-media">
            <picture-lazy v-bind="tab.watch.templateData" class="picture picture-full" />
          </div>

          <phx-text-card
            class="special-models-modal__text-card"
            :title="tab.name"
            :subtitle="tab.ref"
            :content="tab.description"
            :line-clamp="2"
            :read-more-button="true"
            :i18n="{
              readMore: $t('Button.readMore'),
              readLess: $t('Button.readLess'),
            }"
          />

          <div class="special-models-modal__main-cta">
            <p v-if="tab.cluster" class="special-models-modal__main-cta-title title-caption">
              <span>{{ tab.cluster }}</span>
            </p>

            <a :href="tab.href" class="btn btn--primary btn--plain">
              <span class="btn__text">{{ $t('SpecialModel.discoverMore') }}</span>
            </a>
          </div>
        </div>
      </phx-tab-panel>
    </phx-special-models-modal>
  </teleport>
</template>
